
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * BackupUpdateNotify struct
 */
class BackupUpdateNotify {
  /**
   * Initialize struct
   * @param {!number=} version
   * @constructor
   */
  constructor (version = new UInt64(0, 0)) {
    this.Version = version
  }

  /**
   * Copy struct (shallow copy)
   * @this {!BackupUpdateNotify}
   * @param {!BackupUpdateNotify} other Other struct
   * @returns {!BackupUpdateNotify} This struct
   */
  copy (other) {
    if (other.Version != null) {
      this.Version = UInt64.fromNumber(other.Version)
    } else {
      this.Version = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!BackupUpdateNotify}
   * @returns {!BackupUpdateNotify} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new BackupUpdateNotifyModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new BackupUpdateNotifyModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!BackupUpdateNotify}
   * @param {!BackupUpdateNotify} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof BackupUpdateNotify)) {
      throw new TypeError('Instance of BackupUpdateNotify is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!BackupUpdateNotify}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Version: ((this.Version != null) ? this.Version.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return BackupUpdateNotify.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!BackupUpdateNotify} other Object value
   * @returns {!BackupUpdateNotify} Created struct
   */
  static fromObject (other) {
    return new BackupUpdateNotify().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!BackupUpdateNotify}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return BackupUpdateNotify.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!BackupUpdateNotify}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 9003
  }
}

export { BackupUpdateNotify };

/**
 * Fast Binary Encoding BackupUpdateNotify field model
 */
class FieldModelBackupUpdateNotify extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Version = new fbe.FieldModelUInt64(buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelBackupUpdateNotify}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelBackupUpdateNotify}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Version.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelBackupUpdateNotify}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Version.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelBackupUpdateNotify}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelBackupUpdateNotify.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelBackupUpdateNotify}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 9003
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelBackupUpdateNotify}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelBackupUpdateNotify}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Version.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Version.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Version.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelBackupUpdateNotify}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelBackupUpdateNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelBackupUpdateNotify}
   * @param {!BackupUpdateNotify} fbeValue Default value, defaults is new BackupUpdateNotify()
   * @returns {!BackupUpdateNotify} BackupUpdateNotify value
   */
  get (fbeValue = new BackupUpdateNotify()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelBackupUpdateNotify}
   * @param {!BackupUpdateNotify} fbeValue BackupUpdateNotify value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Version.fbeSize) <= fbeStructSize) {
      fbeValue.Version = this.Version.get()
    } else {
      fbeValue.Version = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Version.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelBackupUpdateNotify}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelBackupUpdateNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelBackupUpdateNotify}
   * @param {!BackupUpdateNotify} fbeValue BackupUpdateNotify value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelBackupUpdateNotify}
   * @param {BackupUpdateNotify} fbeValue BackupUpdateNotify value
   */
  setFields (fbeValue) {
    this.Version.set(fbeValue.Version)
  }
}

export { FieldModelBackupUpdateNotify };

/**
 * Fast Binary Encoding BackupUpdateNotify model
 */
class BackupUpdateNotifyModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelBackupUpdateNotify(this.buffer, 4)
  }

  /**
   * Get the BackupUpdateNotify model
   * @this {!BackupUpdateNotifyModel}
   * @returns {!FieldModelBackupUpdateNotify} model BackupUpdateNotify model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!BackupUpdateNotifyModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!BackupUpdateNotifyModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return BackupUpdateNotifyModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!BackupUpdateNotifyModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelBackupUpdateNotify.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!BackupUpdateNotifyModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!BackupUpdateNotifyModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!BackupUpdateNotifyModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!BackupUpdateNotifyModel}
   * @param {!BackupUpdateNotify} value BackupUpdateNotify value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!BackupUpdateNotifyModel}
   * @param {!BackupUpdateNotify} value BackupUpdateNotify value, defaults is new BackupUpdateNotify()
   * @return {!object} Deserialized BackupUpdateNotify value and its size
   */
  deserialize (value = new BackupUpdateNotify()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new BackupUpdateNotify(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new BackupUpdateNotify(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!BackupUpdateNotifyModel}
   * @param {!number} prev Previous BackupUpdateNotify model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { BackupUpdateNotifyModel };
